import { useEffect, useState } from 'react';

import { ApiError, type OrderDetail as Order, type PhotoOut } from '@solar/api-client';
import { openMap } from '@solar/native-bridge';
import {
  APT_TYPE_LABEL,
  ErrorBox,
  Field,
  INSTALL_TYPE_LABEL,
  ORDER_STATE_LABEL,
  Spinner,
  formatDate,
  formatDateTime,
  formatPhone,
  useAsync,
  won,
} from '@solar/ui';

import { PHOTO_SLOTS, api } from '../api';
import { PhotoSlot } from './PhotoSlot';

const INSTALL_TYPES = Object.entries(INSTALL_TYPE_LABEL);

/**
 * 설치 방위 선택지 — iOS SlopeDirectionDataPicker(Constants.swift SLOPE_DIRECTION)
 * 의 rawValue 문자열 그대로. 레거시 DB 에 이 문자열이 그대로 저장된다.
 */
const SLOPE_DIRECTIONS = ['북 0°', '동 90°', '동남 135°', '남 180°', '서남 225°', '서 270°'];

/** 결제 방식 — iOS OrderDetail 결제 버튼 4종(Constants.swift:66-69) 문자열 그대로. */
const PAYMENT_TYPES = ['제로페이', '현금', '카드', '계좌이체'];

/** 레거시 inverter_print_time 은 'HH:mm' 또는 'HH:mm:ss' — time input 용으로 5자로 자른다. */
function toHHmm(value: string | null): string {
  if (!value) return '';
  return value.slice(0, 5);
}

export function OrderDetail({ orderId, onBack }: { orderId: number; onBack: () => void }) {
  const loaded = useAsync(() => api.getOrder(orderId), [orderId]);
  const [order, setOrder] = useState<Order | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setOrder(loaded.data), [loaded.data]);

  if (loaded.loading) return <Spinner />;
  if (loaded.error) return <ErrorBox message={loaded.error} onRetry={loaded.reload} />;
  if (!order) return null;

  function patch(changes: Partial<Order>) {
    setOrder((prev) => (prev ? { ...prev, ...changes } : prev));
    setSaved(false);
  }

  function onUploaded(photo: PhotoOut) {
    setOrder((prev) =>
      prev
        ? { ...prev, photos: [...prev.photos.filter((p) => p.slot !== photo.slot), photo] }
        : prev,
    );
  }

  /**
   * `draft=true` 는 레거시 `temp_save_yn` 을 대체한다.
   * 레거시는 임시저장과 최종저장이 같은 PUT 이라 부분 입력이 그대로 확정되곤 했다.
   */
  async function save(draft: boolean) {
    if (!order) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await api.updateOrder(
        order.id,
        {
          install_date: order.install_date ?? undefined,
          install_type: order.install_type ?? undefined,
          install_complete: (order.install_complete as 'Y' | 'N' | 'C' | null) ?? undefined,
          inverter_print: order.inverter_print ?? undefined,
          inverter_print_time: order.inverter_print_time ?? undefined,
          motion_check_date: order.motion_check_date ?? undefined,
          install_slope_angle: order.install_slope_angle ?? undefined,
          install_slope_direction: order.install_slope_direction ?? undefined,
          payment_check: (order.payment_check as 'Y' | 'N' | null) ?? undefined,
          // 서버는 payment_check='Y' 일 때만 payment_type 을 반영한다(레거시 규칙 유지).
          payment_type: order.payment_type ?? undefined,
          etc: order.etc ?? undefined,
        },
        draft,
      );
      setOrder(updated);
      setSaved(true);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  }

  const address = [order.addr_full, order.building_name, order.apart_number]
    .filter(Boolean)
    .join(' ');
  const missingPhotos = PHOTO_SLOTS.filter(
    (s) => s.required && !order.photos.some((p) => p.slot === s.slot),
  );
  const canComplete = order.install_complete === 'Y' && missingPhotos.length === 0;

  return (
    <div className="shell">
      <header className="shell__head">
        <button type="button" className="link" onClick={onBack}>
          ← 목록
        </button>
        <h1>{order.user_name ?? `#${order.id}`}</h1>
        <span className={`badge s${order.state_type}`}>
          {ORDER_STATE_LABEL[order.state_type] ?? order.state_type}
        </span>
      </header>

      <main className="shell__body">
        <section className="panel">
          <Field label="연락처">
            <a href={`tel:${order.user_phone ?? ''}`}>{formatPhone(order.user_phone)}</a>
          </Field>
          <Field label="주소">
            <button type="button" className="link" onClick={() => void openMap(address)}>
              {address || '-'}
            </button>
          </Field>
          <Field label="건물유형">{APT_TYPE_LABEL[order.apt_type ?? 0] ?? '-'}</Field>
          <Field label="제품">{order.product_name ?? '-'}</Field>
          <Field label="자부담금">{won(order.payment)}</Field>
          <Field label="설치예정일">{formatDate(order.install_date)}</Field>
        </section>

        <h2 className="section">현장 사진</h2>
        <div className="photos">
          {PHOTO_SLOTS.map((s) => (
            <PhotoSlot
              key={s.slot}
              orderId={order.id}
              slot={s.slot}
              label={s.label}
              required={s.required}
              existing={order.photos.find((p) => p.slot === s.slot)}
              onUploaded={onUploaded}
            />
          ))}
        </div>

        <h2 className="section">설치 정보</h2>
        <section className="panel">
          <label className="input">
            설치일
            <input
              type="date"
              value={order.install_date ?? ''}
              onChange={(e) => patch({ install_date: e.target.value })}
            />
          </label>

          <label className="input">
            거치 방식
            <select
              value={order.install_type ?? ''}
              onChange={(e) => patch({ install_type: e.target.value })}
            >
              <option value="">선택</option>
              {INSTALL_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <label className="input">
            인버터 발전량 (kWh)
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              max="1000"
              value={order.inverter_print ?? ''}
              onChange={(e) =>
                patch({ inverter_print: e.target.value === '' ? null : Number(e.target.value) })
              }
            />
          </label>

          <label className="input">
            인버터 출력 확인시각
            <input
              type="time"
              value={toHHmm(order.inverter_print_time)}
              onChange={(e) => patch({ inverter_print_time: e.target.value || null })}
            />
          </label>

          <label className="input">
            가동확인일
            <input
              type="date"
              value={order.motion_check_date ?? ''}
              onChange={(e) => patch({ motion_check_date: e.target.value || null })}
            />
          </label>

          <label className="input">
            경사각 (0~60°)
            <input
              type="number"
              inputMode="numeric"
              step="1"
              min="0"
              max="60"
              value={order.install_slope_angle ?? ''}
              onChange={(e) =>
                patch({
                  install_slope_angle: e.target.value === '' ? null : Number(e.target.value),
                })
              }
            />
          </label>

          <label className="input">
            방위
            <select
              value={order.install_slope_direction ?? ''}
              onChange={(e) => patch({ install_slope_direction: e.target.value || null })}
            >
              <option value="">선택</option>
              {SLOPE_DIRECTIONS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </label>

          <label className="input">
            비고
            <textarea
              rows={3}
              value={order.etc ?? ''}
              onChange={(e) => patch({ etc: e.target.value })}
            />
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={order.install_complete === 'Y'}
              onChange={(e) => patch({ install_complete: e.target.checked ? 'Y' : 'N' })}
            />
            설치 완료
          </label>

          <label className="input">
            결제 방식
            <select
              value={order.payment_type ?? ''}
              onChange={(e) => patch({ payment_type: e.target.value || null })}
            >
              <option value="">선택</option>
              {PAYMENT_TYPES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              {/* 레거시에 다른 문자열이 저장돼 있으면 선택지를 잃지 않게 그대로 노출 */}
              {order.payment_type && !PAYMENT_TYPES.includes(order.payment_type) && (
                <option value={order.payment_type}>{order.payment_type}</option>
              )}
            </select>
          </label>

          <label className="check">
            <input
              type="checkbox"
              checked={order.payment_check === 'Y'}
              onChange={(e) => patch({ payment_check: e.target.checked ? 'Y' : 'N' })}
            />
            자부담금 수납 확인
          </label>

          {/* iOS 는 결제수단 선택 없이 수납 저장이 불가했다 — 미리 경고한다. */}
          {order.payment_check === 'Y' && !order.payment_type && (
            <p className="warn">결제 방식을 선택해주세요. 수납 확인은 결제 방식과 함께 저장됩니다.</p>
          )}
        </section>

        <h2 className="section">진행 이력</h2>
        {order.memos.length === 0 ? (
          <p className="muted">기록된 이력이 없습니다.</p>
        ) : (
          <ul className="cards">
            {/* iOS OrderDetail 하단 이력 테이블(OrderDetailMemoListCell) 파리티 — 읽기 전용 */}
            {order.memos.map((m) => (
              <li key={m.id}>
                <div className="card">
                  <div className="card__top">
                    <strong>{m.user_name ?? '시스템'}</strong>
                    <span className="card__meta">{formatDateTime(m.created_at)}</span>
                  </div>
                  <p className="card__note">{m.memo ?? '-'}</p>
                </div>
              </li>
            ))}
          </ul>
        )}

        {order.contract_count > 0 ? (
          <p className="muted">계약서 {order.contract_count}건이 서명 완료되었습니다.</p>
        ) : (
          <p className="muted">서명된 계약서가 없습니다. 시민에게 발송된 링크로 서명합니다.</p>
        )}

        {saveError && <ErrorBox message={saveError} />}
        {saved && <p className="ok">저장되었습니다.</p>}

        {/* 완료 저장을 막는 이유를 미리 알려준다 — 레거시는 저장 버튼을 누른
            뒤에야 서버 오류 문구로 알려줬다. */}
        {order.install_complete === 'Y' && missingPhotos.length > 0 && (
          <p className="warn">필수 사진 누락: {missingPhotos.map((s) => s.label).join(', ')}</p>
        )}
      </main>

      <footer className="shell__foot">
        <button type="button" onClick={() => void save(true)} disabled={saving}>
          임시저장
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => void save(false)}
          disabled={saving || (order.install_complete === 'Y' && !canComplete)}
        >
          {saving ? '저장 중…' : '저장'}
        </button>
      </footer>
    </div>
  );
}
